import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { emptyData } from '../lib/defaults';
import { demoCars } from '../lib/demo';
import type { Deal } from '../lib/flip';
import { newId } from '../lib/ids';
import { load, save } from '../lib/storage';
import type { AppData, Car, Settings } from '../lib/types';

interface Store {
  data: AppData;
  /** False if the browser refused to save (private mode, storage full). */
  saved: boolean;
  addCar: (title: string, deal: Deal) => Car;
  updateCar: (id: string, patch: Partial<Omit<Car, 'id'>>) => void;
  deleteCar: (id: string) => void;
  setSettings: (s: Settings) => void;
  replaceAll: (d: AppData) => void;
  addDemoCars: () => void;
  eraseAll: () => void;
}

const Ctx = createContext<Store | null>(null);

export function StoreProvider({ children, initial }: { children: ReactNode; initial?: AppData }) {
  const [data, setData] = useState<AppData>(() => initial ?? load());
  const [saved, setSaved] = useState(true);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setSaved(save(data));
  }, [data]);

  const addCar = useCallback((title: string, deal: Deal): Car => {
    const now = new Date().toISOString();
    const car: Car = {
      id: newId(), title: title.trim() || 'Untitled car', reg: '', source: '', status: 'watching',
      planned: structuredClone(deal), deal: structuredClone(deal), boughtOn: '', soldOn: '', notes: '',
      checks: {}, createdAt: now, updatedAt: now,
    };
    setData((d) => ({ ...d, cars: [car, ...d.cars] }));
    return car;
  }, []);

  const updateCar = useCallback((id: string, patch: Partial<Omit<Car, 'id'>>) => {
    setData((d) => ({
      ...d,
      cars: d.cars.map((c) => (c.id === id ? { ...c, ...patch, updatedAt: new Date().toISOString() } : c)),
    }));
  }, []);

  const deleteCar = useCallback((id: string) => {
    setData((d) => ({ ...d, cars: d.cars.filter((c) => c.id !== id) }));
  }, []);

  const value = useMemo<Store>(
    () => ({
      data, saved, addCar, updateCar, deleteCar,
      setSettings: (settings) => setData((d) => ({ ...d, settings })),
      replaceAll: (d) => setData(d),
      addDemoCars: () => setData((d) => ({ ...d, cars: [...demoCars(), ...d.cars] })),
      eraseAll: () => setData(emptyData()),
    }),
    [data, saved, addCar, updateCar, deleteCar],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStore(): Store {
  const s = useContext(Ctx);
  if (!s) throw new Error('useStore must be used inside StoreProvider');
  return s;
}
